import { SearchOptions } from '@giphy/js-fetch-api'
import { IChannel, IGif } from '@giphy/js-types'
import { SearchBar as SDKSearchBar, SearchContext, SearchContextManager } from '@giphy/react-components'
import React, { SyntheticEvent, useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useLatest, useLocalStorage } from 'react-use'
import styled from 'styled-components'
import Categories, { TRENDING } from './components/categories'
import GifDetail from './components/gif-detail'
import GlobalStyles from './components/global-styles'
import Grid from './components/grid'
import Header from './components/header'
import {
    AppContainer,
    CheckeredBackground,
    ContentContainer,
    GifDetailContainer,
    GridContainer,
} from './components/layout'
import { OnboardDialog, TextMessage, EmojiVariationsMessage } from './components/message'
import Nav from './components/nav'
// import SearchBar from './components/search-bar'
import { AppConfig, MARGIN, placeholderCopy } from './constants'
import { AppContext, MessageId, SearchMode } from './context'
import './dnd'

const SearchBar = styled(SDKSearchBar)`
    margin: 0 ${MARGIN}px ${MARGIN}px;
`

type SavedData = {
    savedSearchTerm: string
    videoMuted: boolean
    searchMode: SearchMode
    savedChannel: IChannel | undefined
    savedTrendingState: string[][]
    savedTrendingSearchTerms: string[]
}

export type TestData = SavedData & { gif?: IGif }

type Props = {
    config: AppConfig
    defaultData?: TestData
    onGifClick: (gif: IGif, e: SyntheticEvent<HTMLElement, Event>) => void
    apiKey: string
    onCloseClick?: () => void
    showBorderRadius?: boolean
}

const defaults: SavedData = {
    savedSearchTerm: TRENDING,
    videoMuted: false,
    searchMode: 'gifs',
    savedChannel: undefined,
    savedTrendingState: [],
    savedTrendingSearchTerms: [],
}
const saveTime = 60000 * 2 // 2 minutes of save action

const SearchComponents = ({
    setSavedSearchTerm,
    setSavedChannel,
}: {
    setSavedSearchTerm: (term: string) => void
    setSavedChannel: (channel: IChannel | undefined) => void
}) => {
    const { term, activeChannel, trendingSearches } = useContext(SearchContext)
    const { seenOnboard } = useContext(AppContext)
    const {
        hardCodedCategories,
        savedTrendingSearchTerms,
        displayGif,
        setSavedTrendingSearchTerms,
        searchMode,
        config,
    } = useContext(AppContext)
    useLayoutEffect(() => {
        if (savedTrendingSearchTerms.length === 0 && trendingSearches.length > 0) {
            // wait for trending searches if we don't have it saved
            setSavedTrendingSearchTerms(trendingSearches)
        }
    }, [trendingSearches, setSavedTrendingSearchTerms, savedTrendingSearchTerms])
    useEffect(() => {
        // just for saved state
        setSavedSearchTerm(term)
    }, [term, setSavedSearchTerm])
    useEffect(() => {
        // just for saved state
        setSavedChannel(activeChannel)
    }, [activeChannel, setSavedChannel])
    const clear = [...hardCodedCategories, ...savedTrendingSearchTerms].indexOf(term) !== -1

    return (
        <>
            <Header />
            <SearchBar
                autoFocus
                clear={clear}
                placeholder={searchMode === 'text' ? `Type to Create` : `Search ${placeholderCopy[searchMode]}`}
            />
            <ContentContainer>
                {seenOnboard && <Categories />}
                {seenOnboard && !displayGif && <TextMessage />}
                {seenOnboard && !displayGif && <EmojiVariationsMessage />}
                {!seenOnboard && !displayGif && <OnboardDialog />}
                <GridContainer isVisible={!displayGif}>
                    <CheckeredBackground
                        isStickerGrid={searchMode === 'stickers' || searchMode === 'text'}
                        appWidth={config.width}
                    />
                    <Grid />
                </GridContainer>
                <GifDetailContainer isVisible={!!displayGif}>
                    <GifDetail />
                </GifDetailContainer>
                <Nav />
            </ContentContainer>
        </>
    )
}

let localStorageDisabled = false
try {
    localStorage.setItem('giphy_test_local_storage', true)
    localStorage.removeItem('giphy_test_local_storage')
} catch (error) {
    localStorageDisabled = true
}

const defaultMessagesState = {
    giphyText: localStorageDisabled,
    emojiVariations: localStorageDisabled,
}

const Components = ({
    apiKey,
    setSavedSearchTerm,
    savedSearchTerm,
    setSavedChannel,
    savedChannel,
}: {
    apiKey: string
    savedSearchTerm: string
    savedChannel: IChannel | undefined
    setSavedSearchTerm: (term: string) => void
    setSavedChannel: (channel: IChannel | undefined) => void
}) => {
    const {
        searchMode = 'gifs',
        config: { isBrowserExtension },
    } = useContext(AppContext)
    // let's avoid an immediate second fetch
    const limit = isBrowserExtension ? 17 : 10
    const type = searchMode !== 'emoji' ? searchMode : 'gifs'
    const options: SearchOptions = { type, limit }
    return (
        <SearchContextManager
            apiKey={apiKey}
            options={options}
            initialTerm={savedSearchTerm}
            initialChannel={savedChannel}
            theme={{ searchbarHeight: 34, smallSearchbarHeight: 34, condensedMode: true }}
        >
            <SearchComponents setSavedSearchTerm={setSavedSearchTerm} setSavedChannel={setSavedChannel} />
        </SearchContextManager>
    )
}
const hardCodedCategories = [TRENDING, 'Hello', 'Goodbye', 'Thank you']

export const App = ({ onGifClick, config, apiKey, onCloseClick, showBorderRadius, defaultData }: Props) => {
    // retrieve saved values and defaults
    let [saved = defaults, save] = useLocalStorage<SavedData>('saved', defaults)
    if (defaultData) {
        saved = defaultData
    }

    // retrieve last saved time
    const [lastSavedTime = 0, setLastSavedTime] = useLocalStorage<number>('last-saved-time', 0)

    // if expired use the defaults
    const hasExpired = Date.now() - lastSavedTime > saveTime

    // initialize state with correct data
    const initData = hasExpired && !defaultData ? defaults : saved
    const [savedSearchTerm, setSavedSearchTerm] = useState<string>(initData.savedSearchTerm)
    const [savedChannel, setSavedChannel] = useState<IChannel | undefined>(initData.savedChannel)
    const [searchMode, setSearchMode] = useState<SearchMode>(initData.searchMode)
    const [videoMuted, setVideoMuted] = useState<boolean>(initData.videoMuted)
    const [isOutsideApp, setIsOutsideApp] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const [displayGif, setDisplayGif] = useState<IGif | undefined>(defaultData?.gif)
    const [savedTrendingState = [], setSavedTrendingState] = useState<string[][]>(initData.savedTrendingState)
    const [savedTrendingSearchTerms = [], setSavedTrendingSearchTerms] = useState<string[]>(
        initData.savedTrendingSearchTerms,
    )

    // don't show these at all if there's no local storage
    const [messagesState = defaultMessagesState, setMessagesState] = useLocalStorage<Record<MessageId, boolean>>(
        'features-onboarding',
        undefined,
    )
    const messagesStateRef = useLatest(messagesState)
    const setSeenMessage = useCallback(
        (id: MessageId, seen: boolean) =>
            setMessagesState({
                ...messagesStateRef.current,
                [id]: seen,
            }),
        [setMessagesState, messagesStateRef],
    )
    const seenMessage = useCallback((id: MessageId) => messagesState[id], [messagesState])

    const [seenOnboard = !config.isBrowserExtension || localStorageDisabled, setSeenOnboard] = useLocalStorage<boolean>(
        'onboarding',
        undefined,
    )

    // save values on change
    useEffect(() => {
        save({ savedSearchTerm, searchMode, savedChannel, savedTrendingState, savedTrendingSearchTerms, videoMuted })
        setLastSavedTime(Date.now())
    }, [
        savedSearchTerm,
        searchMode,
        videoMuted,
        save,
        setLastSavedTime,
        savedChannel,
        setSavedTrendingState,
        savedTrendingState,
        savedTrendingSearchTerms,
    ])
    const contextValue = {
        apiKey,
        onCloseClick,
        onGifClick,
        savedSearchTerm,
        setSavedSearchTerm,
        isFetching,
        setIsFetching,
        config,
        displayGif,
        setDisplayGif,
        setSearchMode,
        searchMode,
        videoMuted: isOutsideApp || videoMuted,
        setVideoMuted,
        savedTrendingState,
        setSavedTrendingState,
        savedTrendingSearchTerms,
        setSavedTrendingSearchTerms,
        setSeenMessage,
        seenMessage,
        setSeenOnboard,
        seenOnboard,
        hardCodedCategories,
        categories: [...hardCodedCategories, ...savedTrendingSearchTerms],
    }
    return (
        <>
            <GlobalStyles />
            <AppContext.Provider value={contextValue}>
                <AppContainer
                    config={config}
                    showBorderRadius={showBorderRadius}
                    onMouseLeave={() => setIsOutsideApp(true)}
                    onMouseEnter={() => setIsOutsideApp(false)}
                >
                    <Components
                        apiKey={apiKey}
                        setSavedSearchTerm={setSavedSearchTerm}
                        savedSearchTerm={savedSearchTerm}
                        setSavedChannel={setSavedChannel}
                        savedChannel={savedChannel}
                    />
                </AppContainer>
            </AppContext.Provider>
        </>
    )
}

App.isEmailClient = false
